import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Certain from '../../../../components/Operation/Certain';
import Logo from '../../../../components/Operation/Logo';
import { openOpInfo } from '../../../../components/Popup/PopupOpInfo';
import { selectOpMode, selectOverview } from '../../../../util/store/selectors';
import { getStore, useSelector } from "../../../../util/store/store";
import { eventType, OPERATION_TYPE, serviceTask } from "../../../../util/varibles/constants";
import { getOperationStatus, getStartFinishByOpMode } from "../../../../util/varibles/global";
import { IOperation, ITaskService } from "../../../../util/varibles/interface";
import { getTypesAndUnitOfTreatment } from '../../../PlanOperationPage/util';
import { HEIGHT_PER_OP } from "../Vessels";
import { IPropsOp } from "./index";
import styles from './style.module.scss';

const descriptionView: Record<string, (data: IOperation) => ReactNode> = {
    [OPERATION_TYPE.EVENT]: (data) => {
        const { event_type } = data.operation;
        const { name = '' } = eventType[event_type] || {}
        return <div className='text-nowrap'>{name}</div>;
    },
    [OPERATION_TYPE.HARVEST]: (data) => {
        const { operation } = data;
        return <>
            <div className='text-nowrap'>{operation.site_name}</div>
            <div className='arrow-right' />
            <div className='text-nowrap'>{operation.factory_name}</div>
        </>
    },
    [OPERATION_TYPE.SERVICE]: (data) => {
        const { site_name, sites = [], tasks = [] } = data.operation;
        const location = sites.length === 0
            ? site_name
            : sites.map((item: any) => item.name).join(' | ')
        const taskTypes = tasks.reduce((rs: { [id: string]: string | undefined }, item: ITaskService) => {
            const { type = "" } = item;
            rs[type] = item.name
            return rs
        }, {})
        const jobNames = Object.keys(taskTypes).map(key => {
            const name = taskTypes[key] || serviceTask[key || ""]?.name || " "
            return name;
        }).join(', ')
        return <div className='text-nowrap'>{location} - {jobNames}</div>
    },
    [OPERATION_TYPE.SUPPORT]: () => {
        return <div>Support</div>
    },
    [OPERATION_TYPE.TRANSPORT]: ({ operation }) => {
        return <>
            <div className='text-nowrap'>{operation.source_name}</div>
            <div className='arrow-right' />
            <div className='text-nowrap'>{operation.destination_name}</div>
        </>
    },
    [OPERATION_TYPE.TREATMENT]: (data) => {
        const { site_name } = data.operation;
        const { types } = getTypesAndUnitOfTreatment(data.operation.tasks);
        return <div className='text-nowrap'>{site_name} - {Object.keys(types).map(k => types[k]).join(", ")} ({data.operation.sub_operations.map(item => item.unit_id).join(", ")})</div>
    }
}

interface IProps extends IPropsOp {
    data: IOperation
}

const WrapperOperation: React.FC<IProps> = ({ data, level, vesselHeight }) => {
    const operation_mode = useSelector(selectOpMode);
    const rootRef = useRef<HTMLDivElement>(null);
    const [description, setDescription] = useState<ReactNode | undefined>();
    const [minWidth, setMinWidth] = useState<string | undefined>();

    const open = useCallback(() => {
        const { related_id: mainId = "" } = data.operation;
        let spOps: IOperation[] = [];
        if (mainId.length > 0) {
            const ops = selectOverview.ops(getStore());
            spOps = Object.keys(ops).reduce((rs: IOperation[], key) => {
                const { operation_type, related_id } = ops[key].operation;
                if (operation_type === OPERATION_TYPE.SUPPORT && related_id === mainId) {
                    return [...rs, ops[key]]
                }
                return rs;
            }, [])
        }
        openOpInfo({ isEdit: false, data, spOps })
    }, [data]);

    useEffect(() => {
        const { [`${data.operation.operation_type}`]: descriptionFn } = descriptionView || {};
        const description = descriptionFn?.(data);
        setDescription(description);
        setTimeout(() => {
            if (rootRef.current) {
                const { childNodes } = rootRef.current;
                let minWidth = 12;
                childNodes.forEach(element => {
                    const { offsetWidth } = element as HTMLElement;
                    minWidth += (offsetWidth + 2);
                });
                setMinWidth(`${minWidth}px`)
            }
        }, 10)
    }, [data])


    if (!vesselHeight[data.vessel.id])
        return null;

    const { start, finish } = getStartFinishByOpMode[operation_mode](data.activity_log, data.operation.status);

    const vesselId = data.vessel.id;
    const levelOffset = level.main ? 0 : level.level * HEIGHT_PER_OP;

    const { operation_type, event_type, status, pauses } = data.operation;
    const realStatus = getOperationStatus(status, pauses);

    const style = {
        '--op-left': start,
        '--op-width': finish - start,
        '--op-min-width': minWidth,
        top: `calc(var(--${vesselId}-operation-top) + ${levelOffset}px)`,
    }

    return <div
        id={data.operation.id}
        ref={rootRef}
        className={styles.wrapper}
        style={style}
        onClick={open}
    >
        <Logo type={operation_type} status={realStatus} dataAttribute={{ 'data-event-type': event_type }} />
        <Certain operation={data.operation} status={status} isLabel={false} />
        <div className="flex text-[14px] ml-[5px] items-center">
            {description}
        </div>
    </div>;
};

export default WrapperOperation;
